import { X } from 'lucide-react';
import { useId, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';

import { cn } from '../lib/cn';
import { Button } from './Button';

export interface TagInputProps {
  label?: string | undefined;
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  max?: number | undefined;
  maxReachedMessage?: string | undefined;
  disallowedValues?: string[] | undefined;
  transformValue?: ((raw: string) => string) | undefined;
  addOnBlur?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

const defaultTransformValue = (raw: string) => raw.trim();

export function TagInput({
  label,
  value,
  onValueChange,
  placeholder = 'Add tag',
  disabled = false,
  max,
  maxReachedMessage,
  disallowedValues = [],
  transformValue = defaultTransformValue,
  addOnBlur = true,
  id,
  className,
}: TagInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [draftTag, setDraftTag] = useState('');
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  const disallowedSet = useMemo(() => new Set(disallowedValues), [disallowedValues]);
  const isAtMax = max != null && value.length >= max;
  const isInputDisabled = disabled || isAtMax;

  function canAddTag(tag: string, currentTags: string[] = value): boolean {
    if (!tag) {
      return false;
    }

    if (currentTags.includes(tag)) {
      return false;
    }

    if (disallowedSet.has(tag)) {
      return false;
    }

    return max == null || currentTags.length < max;
  }

  function normalizeTags(input: string): string[] {
    const seen = new Set<string>();
    const normalized: string[] = [];

    for (const raw of input.split(/[,\n]/g)) {
      const tag = transformValue(raw);
      if (!canAddTag(tag, [...value, ...normalized]) || seen.has(tag)) {
        continue;
      }

      seen.add(tag);
      normalized.push(tag);
    }

    return normalized;
  }

  function commitDraft(input: string) {
    const nextTags = normalizeTags(input);
    setDraftTag('');

    if (!nextTags.length) {
      return;
    }

    onValueChange([...value, ...nextTags]);
  }

  function removeTag(tag: string) {
    onValueChange(value.filter((currentTag) => currentTag !== tag));
  }

  function startEditing(tag: string) {
    if (disabled) {
      return;
    }

    setEditingTag(tag);
    setEditingValue(tag);
  }

  function commitEditing() {
    if (!editingTag) {
      return;
    }

    const nextTag = transformValue(editingValue);
    const otherTags = value.filter((tag) => tag !== editingTag);

    if (!nextTag || otherTags.includes(nextTag) || disallowedSet.has(nextTag)) {
      setEditingTag(null);
      setEditingValue('');
      return;
    }

    if (max != null && otherTags.length >= max) {
      setEditingTag(null);
      setEditingValue('');
      return;
    }

    onValueChange(
      value.map((tag) => {
        if (tag === editingTag) {
          return nextTag;
        }

        return tag;
      }),
    );
    setEditingTag(null);
    setEditingValue('');
  }

  function cancelEditing() {
    setEditingTag(null);
    setEditingValue('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitDraft(draftTag);
      return;
    }

    if (event.key === 'Backspace' && !draftTag && value.length) {
      event.preventDefault();
      removeTag(value[value.length - 1] ?? '');
    }
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData('text');
    if (!/[,\n]/.test(pasted)) {
      return;
    }

    event.preventDefault();
    commitDraft(draftTag ? `${draftTag},${pasted}` : pasted);
  }

  return (
    <div className={cn('grid gap-2', className)}>
      {label ? (
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          'border-input focus-within:ring-ring flex min-h-10 w-full flex-wrap items-center gap-2 rounded-md border bg-transparent px-3 py-2 shadow-(--shadow-control) transition-colors focus-within:ring-1',
          (disabled || isAtMax) && 'cursor-not-allowed opacity-50',
        )}
      >
        {value.map((tag) =>
          editingTag === tag ? (
            <input
              key={tag}
              value={editingValue}
              disabled={disabled}
              aria-label={`Edit ${tag}`}
              className="border-border bg-muted text-foreground h-7 min-w-16 rounded-md border px-2 text-sm outline-none"
              onChange={(event) => {
                setEditingValue(event.target.value);
              }}
              onBlur={() => {
                commitEditing();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitEditing();
                }

                if (event.key === 'Escape') {
                  event.preventDefault();
                  cancelEditing();
                }
              }}
            />
          ) : (
            <span
              key={tag}
              className="border-border bg-muted text-foreground inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-1 text-sm"
            >
              <button
                type="button"
                className="truncate text-left"
                disabled={disabled}
                onDoubleClick={() => {
                  startEditing(tag);
                }}
              >
                {tag}
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5 rounded-full"
                aria-label={`Remove ${tag}`}
                disabled={disabled}
                onClick={() => {
                  removeTag(tag);
                }}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </Button>
            </span>
          ),
        )}
        <input
          ref={inputRef}
          id={inputId}
          value={draftTag}
          disabled={isInputDisabled}
          aria-label={label}
          placeholder={value.length ? undefined : placeholder}
          className="placeholder:text-muted-foreground min-w-24 flex-1 border-0 bg-transparent px-0 py-0 text-sm outline-none disabled:cursor-not-allowed"
          onChange={(event) => {
            setDraftTag(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            if (addOnBlur) {
              commitDraft(draftTag);
            }
          }}
        />
      </div>
      {isAtMax && maxReachedMessage ? (
        <p className="text-muted-foreground text-xs">{maxReachedMessage}</p>
      ) : null}
    </div>
  );
}
